import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { hrApi } from '@/services/api/hr'
import { ApiErrorResponse } from '@/services/api/client'
import type {
  CostBearerRow, DeductionKind, HrDeskTeacher, HrDeskTeacherDetail, HrFinanceCase,
  HrFinanceLessonRow, PayoutKind,
} from '@/types/hr'
import { extractErrorMessage } from '@/utils/errorHandler'
import { absenceLabel, formatHrDate, isModuleInactiveError } from '@/utils/hrLabels'
import { HrModuleInactiveNotice } from '@/components/hr/HrModuleInactiveNotice'
import { SettleOverrideDialog } from '@/components/hr/SettleOverrideDialog'
import { FloatInput } from '@/components/ui/FloatInput'
import { absenceTypesApi } from '@/services/api/absenceTypes'
import { DEFAULT_ABSENCE_OPTIONS } from '@/hooks/useAbsenceTypes'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'

function fmt(n: number): string {
  return n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function saldoClass(n: number) {
  return n > 0 ? 'text-green-600' : n < 0 ? 'text-red-600' : 'text-gray-500'
}

function isNotFoundError(e: unknown): boolean {
  if (e instanceof ApiErrorResponse) return e.statusCode === 404
  return (e as { statusCode?: number }).statusCode === 404
    || (e as { response?: { status?: number } }).response?.status === 404
}

interface Draft {
  payout: PayoutKind
  deduction: DeductionKind
  amount: number | null
}

function defaultDraft(row: HrFinanceLessonRow): Draft {
  return {
    payout: row.default_payout,
    deduction: row.requester_deduction ?? 'ipb',
    amount: row.amount_default ?? 1,
  }
}

function rowWash(row: HrFinanceLessonRow): string {
  if (row.role === 'substitute') return 'bg-green-50 hover:bg-green-100/80'
  if (row.role === 'requester' && row.payer === 'private') return 'bg-red-50 hover:bg-red-100/80'
  if (row.status === 'limit') return 'bg-red-50 hover:bg-red-100/80'
  return 'hover:bg-gray-50'
}

function draftEquals(a: Draft, b: Draft): boolean {
  return a.payout === b.payout && a.deduction === b.deduction && a.amount === b.amount
}

function canBookRow(row: HrFinanceLessonRow): boolean {
  return row.status !== 'limit' && !(row.role === 'requester' && row.payer !== 'private')
}

function canCloseNoDeduction(row: HrFinanceLessonRow): boolean {
  return row.status !== 'limit' && row.role === 'requester' && row.payer !== 'private'
}

function rowToCase(row: HrFinanceLessonRow): HrFinanceCase {
  return {
    substitution_request_id: row.substitution_request_id,
    earliest_lesson_date: row.date,
    open_lessons: 1,
    subject_groups: [{
      subject_id: null,
      subject_name: row.subject_name,
      lesson_ids: [row.lesson_id],
      default_payout: row.default_payout,
      payout_source: row.payout_source,
    }],
    lessons: [{
      id: row.lesson_id,
      date: row.date,
      slot: row.slot,
      subject_name: row.subject_name,
      assigned_teacher_id: row.substitute_id,
      assigned_teacher_name: row.substitute_name,
      self_choice: row.self_choice,
      default_payout: row.default_payout,
      payout_source: row.payout_source,
    }],
    requester_teacher_id: row.requester_id,
    requester_name: row.requester_name,
    substitutes: [row.substitute_name],
    absence_type: row.absence_type,
    absence_type_label: row.absence_type_label,
    payer: row.payer,
    requester_deduction: row.requester_deduction,
    open_choice: false,
    decider_substitute: 'hr',
    warnings: row.warning ? [row.warning] : [],
  }
}

function Seg({
  value, options, onChange, tone,
}: {
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  tone: 'sub' | 'req'
}) {
  const on = tone === 'sub' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
  return (
    <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden bg-white">
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`text-[11px] font-semibold px-2 py-1 ${value === o.value ? on : 'text-gray-500'}`}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function HRLehrerpultPage() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const teacherParam = params.get('teacher') || ''
  const [filter, setFilter] = useState<'open' | 'all'>('open')
  const [qInput, setQInput] = useState('')
  const [q, setQ] = useState('')
  const [teachers, setTeachers] = useState<HrDeskTeacher[]>([])
  const [allCount, setAllCount] = useState(0)
  const [openCount, setOpenCount] = useState(0)
  const [detail, setDetail] = useState<HrDeskTeacherDetail | null>(null)
  const [bearers, setBearers] = useState<CostBearerRow[]>([])
  const [ipbOn, setIpbOn] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [moduleInactive, setModuleInactive] = useState(false)
  const [editRow, setEditRow] = useState<HrFinanceLessonRow | null>(null)
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})

  const loadList = useCallback(async () => {
    try {
      const allList = await hrApi.getDesk({ q: q || undefined, filter: 'all' })
      const openTeachers = allList.teachers.filter(t => t.open_count > 0)
      setOpenCount(openTeachers.length)
      setAllCount(allList.total)
      setTeachers(filter === 'open' ? openTeachers : allList.teachers)
      setModuleInactive(false)
      setError(null)
    } catch (e: unknown) {
      if (isModuleInactiveError(e)) setModuleInactive(true)
      else setError(extractErrorMessage(e, { loadError: 'Lehrerpult konnte nicht geladen werden' }))
    } finally { setLoading(false) }
  }, [q, filter])

  const loadDetail = useCallback(async (id: string) => {
    if (!id) { setDetail(null); return }
    try {
      const d = await hrApi.getDeskTeacher(id)
      setDetail(d)
      setDrafts({})
      setError(null)
    } catch (e: unknown) {
      if (isModuleInactiveError(e)) setModuleInactive(true)
      else if (isNotFoundError(e)) {
        setDetail(null)
        setParams({}, { replace: true })
      } else {
        setDetail(null)
        setError(extractErrorMessage(e, { loadError: 'Lehrerdetails konnten nicht geladen werden' }))
      }
    }
  }, [setParams])

  useEffect(() => {
    const t = window.setTimeout(() => setQ(qInput), 300)
    return () => window.clearTimeout(t)
  }, [qInput])
  useEffect(() => { void loadList() }, [loadList])
  useEffect(() => { void loadDetail(teacherParam) }, [teacherParam, loadDetail])
  useEffect(() => {
    hrApi.getCostBearers().then(r => setBearers(r.cost_bearers)).catch(() => {})
    hrApi.getMyScopes().then(s => setIpbOn(s.pages.ipb_payouts)).catch(() => {})
  }, [])

  const selectTeacher = (id: string) => {
    setParams(id ? { teacher: id } : {}, { replace: true })
  }

  if (moduleInactive) return <HrModuleInactiveNotice title="Lehrerpult" />
  if (loading) return <div className="p-6 text-gray-500">Laden...</div>

  const draftOf = (row: HrFinanceLessonRow): Draft => drafts[row.lesson_id] ?? defaultDraft(row)
  const patchDraft = (row: HrFinanceLessonRow, patch: Partial<Draft>) => {
    setDrafts(d => ({ ...d, [row.lesson_id]: { ...draftOf(row), ...patch } }))
  }

  const settleRow = async (row: HrFinanceLessonRow) => {
    const d = draftOf(row)
    const orig = defaultDraft(row)
    setBusy(true); setError(null); setNotice(null)
    try {
      const r = await hrApi.settleLessons([{
        lesson_id: row.lesson_id,
        payout: row.role === 'substitute' && d.payout !== orig.payout ? d.payout : undefined,
        requester_deduction: row.role === 'requester' && row.payer === 'private' && d.deduction !== orig.deduction
          ? d.deduction : undefined,
        amount: d.amount != null && d.amount > 0 && d.amount !== orig.amount ? d.amount : undefined,
      }])
      const problems = [...r.skipped, ...r.failed]
      if (problems.length) setError(problems.map(s => s.detail).join(' '))
      if (r.settled_lessons) setNotice(`${r.settled_lessons} Lektion(en) verbucht.`)
      await loadList()
      if (teacherParam) await loadDetail(teacherParam)
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Abrechnung fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  const unchangedRows = (detail?.open_lessons ?? []).filter(l =>
    l.status === 'default'
    && (canBookRow(l) || canCloseNoDeduction(l))
    && draftEquals(draftOf(l), defaultDraft(l)))
  const unchangedCount = new Set(unchangedRows.map(l => l.lesson_id)).size

  const settleUnchanged = async () => {
    if (!teacherParam || unchangedCount === 0) return
    setBusy(true); setError(null); setNotice(null)
    try {
      const seen = new Set<string>()
      const items = unchangedRows.filter(l => {
        if (seen.has(l.lesson_id)) return false
        seen.add(l.lesson_id)
        return true
      }).map(l => ({ lesson_id: l.lesson_id }))
      const r = await hrApi.settleLessons(items)
      const problems = [...r.skipped, ...r.failed]
      if (problems.length) setError(problems.map(s => s.detail).join(' '))
      if (r.settled_lessons) setNotice(`Unveränderte: ${r.settled_lessons} Lektion(en) verbucht.`)
      await loadList()
      await loadDetail(teacherParam)
    } catch (e: unknown) {
      setError(extractErrorMessage(e, { loadError: 'Abrechnung fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  return (
    <div className="flex flex-col min-h-full">
      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-5 overflow-hidden">
        <div className="lg:col-span-2 border-r bg-white overflow-y-auto">
          <div className="p-3 border-b sticky top-0 bg-white z-10">
            <input value={qInput} onChange={e => setQInput(e.target.value)}
              className="w-full text-sm border rounded-md px-2.5 py-1.5" placeholder="Lehrer suchen…" />
            {teacherParam && (
              <RetroNachtragButton teacherId={teacherParam} onDone={() => { loadList(); if (teacherParam) loadDetail(teacherParam) }} />
            )}
            <div className="flex gap-2 mt-2 text-[11px] text-gray-500">
              <button type="button" onClick={() => setFilter('open')}
                className={`px-2 py-0.5 rounded-full font-medium ${filter === 'open' ? 'bg-indigo-50 text-indigo-700' : ''}`}>
                mit offenen {openCount}
              </button>
              <button type="button" onClick={() => setFilter('all')}
                className={`px-2 py-0.5 rounded-full ${filter === 'all' ? 'bg-indigo-50 text-indigo-700 font-medium' : ''}`}>
                alle {allCount}
              </button>
            </div>
          </div>
          {teachers.map(t => (
            <button key={t.teacher_id} type="button" onClick={() => selectTeacher(t.teacher_id)}
              className={`w-full text-left px-3 py-2.5 flex justify-between border-l-4 ${
                teacherParam === t.teacher_id ? 'bg-indigo-50 border-indigo-600' : 'border-transparent hover:bg-gray-50'
              }`}>
              <div>
                <div className="text-sm font-medium">{t.teacher_name}</div>
                <div className={`text-xs ${t.negative_limit ? 'text-red-600' : 'text-gray-500'}`}>
                  {t.teacher_code ? `${t.teacher_code} · ` : ''}
                  {t.open_count > 0 ? `${t.open_count} offene Lekt.` : 'keine offenen'}
                  {t.self_choice ? ' · Selbst-Wahl' : ''}
                  {t.payout ? ' · Auszahlung' : ''}
                  {t.negative_limit ? ' · Negativ-Limit' : ''}
                </div>
              </div>
              <div className={`text-sm font-bold ${saldoClass(t.saldo)}`}>
                {t.saldo > 0 ? '+' : ''}{fmt(t.saldo)}
              </div>
            </button>
          ))}
          {teachers.length === 0 && (
            <div className="px-3 py-6 text-sm text-gray-500 text-center">Keine Lehrer</div>
          )}
        </div>
        <div className="lg:col-span-3 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {error && <div className="p-3 bg-red-100 text-red-700 rounded text-sm">{error}</div>}
          {notice && <div className="p-3 bg-green-100 text-green-700 rounded text-sm">{notice}</div>}
          {!detail && (
            <div className="p-10 text-center text-gray-500 text-sm">Lehrer wählen</div>
          )}
          {detail && (
            <>
              <div className="bg-white rounded-xl border p-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">{detail.teacher_name}
                    {detail.teacher_code && <span className="text-sm font-normal text-gray-400 ml-2">{detail.teacher_code}</span>}
                  </h3>
                  <div className="text-sm">
                    IPB <span className={`font-bold ${saldoClass(detail.saldo)}`}>{detail.saldo > 0 ? '+' : ''}{fmt(detail.saldo)} Lekt.</span>
                    {' · '}Periode {detail.period_delta > 0 ? '+' : ''}{fmt(detail.period_delta)}
                    {' · '}Auszahlung {fmt(detail.payout_lessons)}
                  </div>
                </div>
                {ipbOn && (
                  <button type="button"
                    onClick={() => navigate(`/hr/ipb?tab=statement&teacher=${detail.teacher_id}`)}
                    className="text-xs border rounded-md px-2.5 py-1.5">Kontoauszug</button>
                )}
              </div>
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-4 py-2.5 border-b font-semibold text-sm flex justify-between items-center">
                  Offene Lektionen dieser Person
                  <button type="button" disabled={busy || unchangedCount === 0} onClick={() => void settleUnchanged()}
                    className="text-xs bg-indigo-600 text-white rounded-md px-2.5 py-1 disabled:opacity-50">
                    Unveränderte verbuchen{unchangedCount ? ` (${unchangedCount})` : ''}
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">Lektion</th>
                      <th className="text-left px-4 py-2">Rolle</th>
                      <th className="text-left px-4 py-2">Buchung</th>
                      <th className="text-left px-3 py-2 w-16">Lekt.</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white">
                    {detail.open_lessons.length === 0 && (
                      <tr><td colSpan={5} className="px-4 py-4 text-center text-gray-500">Keine offenen Lektionen</td></tr>
                    )}
                    {detail.open_lessons.map(row => {
                      const d = draftOf(row)
                      const canBook = canBookRow(row)
                      const canClose = canCloseNoDeduction(row)
                      const showPencil = canBook || row.status === 'limit'
                      const roleClass = row.role === 'substitute' ? 'text-green-800' : row.payer === 'private' ? 'text-red-800' : 'text-gray-600'
                      return (
                        <tr key={`${row.lesson_id}-${row.role}`} className={rowWash(row)}>
                          <td className="px-4 py-2.5">
                            <div className="font-medium">{row.subject_name}{row.class_name && row.class_name !== '—' ? ` ${row.class_name}` : ''}</div>
                            <div className="text-xs text-gray-500">
                              {formatHrDate(row.date)}
                              {row.start_time ? ` · ${row.start_time}${row.end_time ? `–${row.end_time}` : ''}` : ''}
                              {' · '}{row.role === 'substitute' ? row.requester_name : row.substitute_name}
                              {' · '}{row.absence_type_label || absenceLabel(row.absence_type)}
                              {' · '}{row.payer === 'private' ? 'privat' : 'Schule'}
                            </div>
                          </td>
                          <td className={`px-4 py-2.5 text-xs font-medium ${roleClass}`}>
                            {row.role === 'substitute' ? 'Vertreter' : 'Anfragend'}
                          </td>
                          <td className="px-4 py-2.5">
                            {row.status === 'limit' ? (
                              <span className="text-[11px] px-2 py-0.5 rounded font-semibold bg-red-100 text-red-700">Limit</span>
                            ) : row.role === 'substitute' ? (
                              <Seg tone="sub" value={d.payout}
                                onChange={v => patchDraft(row, { payout: v as PayoutKind })}
                                options={[{ value: 'lesson_account', label: 'IPB' }, { value: 'paid_out', label: 'Auszahlung' }]} />
                            ) : row.payer === 'private' ? (
                              <Seg tone="req" value={d.deduction}
                                onChange={v => patchDraft(row, { deduction: v as DeductionKind })}
                                options={[{ value: 'ipb', label: 'IPB' }, { value: 'salary', label: 'Lohn' }]} />
                            ) : (
                              <span className="text-xs text-gray-400">keine Abbuchung</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            {canBook ? (
                              <FloatInput value={d.amount} allowEmpty
                                onChange={v => patchDraft(row, { amount: v })}
                                className="w-14 text-sm border border-gray-300 rounded px-1.5 py-1 bg-white" />
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {showPencil && (
                            <button type="button" disabled={busy} onClick={() => setEditRow(row)}
                              title="Anpassen"
                              className="w-7 h-7 inline-flex items-center justify-center border border-gray-300 rounded-md bg-white mr-1 hover:bg-gray-50">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>
                            </button>
                            )}
                            {canBook && (
                              <button type="button" disabled={busy} onClick={() => void settleRow(row)}
                                title="Verbuchen"
                                className={`w-8 h-8 inline-flex items-center justify-center rounded-md text-white ${
                                  row.role === 'substitute' ? 'bg-green-600' : 'bg-red-600'
                                } disabled:opacity-50`}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M5 12l5 5L20 7" /></svg>
                              </button>
                            )}
                            {canClose && (
                              <button type="button" disabled={busy} onClick={() => void settleRow(row)}
                                title="Erledigt"
                                className="h-8 px-2.5 inline-flex items-center justify-center rounded-md text-xs font-semibold text-white bg-gray-500 disabled:opacity-50">
                                Erledigt
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <div className="bg-white rounded-xl border p-3">
                <div className="text-xs font-bold text-gray-400 uppercase mb-2">Letzte Buchungen</div>
                <div className="text-xs text-gray-600 space-y-1">
                  {(detail.recent_transactions || []).length === 0 && <div className="text-gray-400">Keine Buchungen</div>}
                  {(detail.recent_transactions || []).map(tx => (
                    <div key={tx.id} className="flex justify-between">
                      <span>{tx.created_at ? formatHrDate(tx.created_at.slice(0, 10)) : ''} {tx.reason || tx.subject_name || tx.tx_type}</span>
                      <span className={saldoClass(tx.amount)}>{tx.amount > 0 ? '+' : ''}{fmt(tx.amount)} → {fmt(tx.running_saldo)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {editRow && (
        <SettleOverrideDialog
          caseData={rowToCase(editRow)}
          bearers={bearers}
          lessonIds={[editRow.lesson_id]}
          onClose={() => setEditRow(null)}
          onSettled={async (msg) => {
            setEditRow(null); setNotice(msg)
            await loadList()
            if (teacherParam) await loadDetail(teacherParam)
          }}
        />
      )}
    </div>
  )
}

function RetroNachtragButton({ teacherId, onDone }: { teacherId: string; onDone: () => void }) {
  const directorEnabled = useDirectorEnabled()
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState('')
  const [absenceType, setAbsenceType] = useState('other')
  const [paymentType, setPaymentType] = useState<'private' | 'school'>('private')
  const [lessons, setLessons] = useState<{ entry_id: string; subject_name: string | null }[]>([])
  const [picked, setPicked] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [absenceTypes, setAbsenceTypes] = useState(DEFAULT_ABSENCE_OPTIONS)
  const yesterday = (() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  })()

  useEffect(() => {
    if (!open) return
    absenceTypesApi.list().then(r => {
      if (r.absence_types?.length) setAbsenceTypes(r.absence_types)
    }).catch(() => { /* fallback defaults */ })
  }, [open])

  const loadLessons = async (d: string) => {
    setDate(d)
    if (!d) { setLessons([]); return }
    try {
      const r = await hrApi.retroLessons(teacherId, d)
      setLessons(r.lessons)
      setPicked(r.lessons.map(l => l.entry_id))
    } catch (e) {
      setErr(extractErrorMessage(e, { loadError: 'Lektionen konnten nicht geladen werden' }))
    }
  }

  const submit = async (routing: 'self' | 'director') => {
    if (!date || picked.length === 0) return
    setBusy(true); setErr(null)
    try {
      await hrApi.createRetro({
        absent_teacher_id: teacherId,
        entry_ids: picked,
        dates: [date],
        absence_type: directorEnabled ? absenceType : 'other',
        payment_type: directorEnabled ? paymentType : 'school',
        routing: directorEnabled ? routing : 'self',
      })
      setOpen(false)
      onDone()
    } catch (e) {
      setErr(extractErrorMessage(e, { loadError: 'Nachtrag fehlgeschlagen' }))
    } finally { setBusy(false) }
  }

  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="mt-2 w-full text-xs font-medium text-indigo-700 bg-indigo-50 rounded-md py-1.5">
        Nachtrag erfassen
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-4 w-full max-w-md shadow-lg">
            <h3 className="text-sm font-semibold mb-3">Nachtrag Vergangenheit</h3>
            {err && <p className="text-xs text-red-600 mb-2">{err}</p>}
            <label className="text-xs text-gray-500">Tag</label>
            <input type="date" max={yesterday} value={date} onChange={e => loadLessons(e.target.value)}
              className="w-full border rounded-md px-2 py-1.5 text-sm mb-2" />
            {directorEnabled && (
              <>
                <label className="text-xs text-gray-500">Absenzart</label>
                <select value={absenceType} onChange={e => setAbsenceType(e.target.value)}
                  className="w-full border rounded-md px-2 py-1.5 text-sm mb-2">
                  {absenceTypes.map(t => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
                <div className="flex gap-3 text-sm mb-2">
                  <label><input type="radio" checked={paymentType === 'private'} onChange={() => setPaymentType('private')} /> Privat</label>
                  <label><input type="radio" checked={paymentType === 'school'} onChange={() => setPaymentType('school')} /> Schule</label>
                </div>
              </>
            )}
            <ul className="max-h-32 overflow-y-auto text-xs mb-3">
              {lessons.map(l => (
                <li key={l.entry_id}>
                  <label>
                    <input type="checkbox" checked={picked.includes(l.entry_id)}
                      onChange={() => setPicked(p => p.includes(l.entry_id) ? p.filter(x => x !== l.entry_id) : [...p, l.entry_id])} />
                    {' '}{l.subject_name || 'Lektion'}
                  </label>
                </li>
              ))}
            </ul>
            <div className="flex gap-2">
              <button type="button" disabled={busy} onClick={() => submit('self')}
                className="flex-1 bg-indigo-600 text-white rounded-md py-1.5 text-xs">Selbst abwickeln</button>
              {directorEnabled && (
                <button type="button" disabled={busy} onClick={() => submit('director')}
                  className="flex-1 bg-amber-600 text-white rounded-md py-1.5 text-xs">An Direktor</button>
              )}
              <button type="button" onClick={() => setOpen(false)} className="text-xs px-2">Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default HRLehrerpultPage
